// @givmo/compliance — "Who files it" and Givmo’s filing desk on the panel (CCM-R1-DESK-WEB;
// REVIEW-35's desk-01 to desk-08, desk-09b, desk-13b and desk-17 to desk-22, D-CCM-83).
//
// Frame 21a's row shows only where Givmo can file the filing under an agreement that covers it (Q1).
// While Givmo has the filing, the row's status is the desk's line and its acts (Q4), with who handed
// it over under it (N4). Choosing "Givmo handles it" asks first (Q2); so does taking it back (Q3).
// With Givmo's AI off the choice opens frame 21 or 21b instead. Every act is offered only where the
// read says the viewer may take it, and a refusal shows as the act's failure line (a race).

import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  ErrorMessage,
  RadioTile,
  RadioTileGroup,
  testIdProp,
} from '@givmo/design-system';
import { complianceAiSettingQueryKey, decodeComplianceAiSetting } from './compliance-ai-setting';
import { useCompliance } from './compliance-context';
import { isGivmoAiOff, serverMessage, type ComplianceObligation } from './compliance-decode';
import {
  decodeDeskCase,
  decodeDeskStanding,
  deskHolds,
  deskOffered,
  deskStatusLine,
  needsAuthorization,
  type DeskCase,
} from './compliance-desk-model';
import type { NextDate } from './compliance-filings-model';
import { decodeOpenedFilingCase, extractFilingCases } from './compliance-preparation-decode';
import {
  OPEN_THE_FORM,
  caseForTarget,
  openCaseBody,
  preparationTargetFor,
} from './compliance-preparation-model';
import {
  deskStandingQueryKey,
  filingCasesQueryKey,
  getCaseSubmission,
  getDeskStanding,
  listAllFilingCases,
  submissionQueryKey,
} from './compliance-queries';
import { shortDateIn } from './compliance-signing-model';
import { decodeSubmission } from './compliance-submission-decode';
import { followThroughOf } from './compliance-results-model';
import { CANCEL, submissionStage } from './compliance-submission-model';
import {
  AI_OFF_LINE,
  AI_OFF_TITLE,
  GIVMO_HANDLES_IT,
  GIVMO_HANDLES_IT_LINE,
  GOT_IT,
  GO_TO_SETTINGS,
  N1,
  N13,
  N2,
  N3,
  N5,
  N6,
  N7,
  N8,
  ONLY_OWNERS_TURN_ON,
  RUN_BY_AI,
  WE_HANDLE_IT,
  R5,
  WE_HANDLE_IT_LINE,
  W3C_NEED,
  WHO_FILES_IT,
  n4,
  w3,
} from './desk-words';

const PREFIX = 'compliance-desk';
/** The landed failure line, where the server gives none. */
const TRY_AGAIN_LINE = 'Please try again.';

/** What the panel knows of Givmo's desk on a filing's year, read once for the panel. */
export interface PanelDesk {
  /** Q1: whether "Who files it" shows. */
  offered: boolean;
  /** Whether Givmo's desk has the filing now (Q12 hides the organization's own ways). */
  holds: boolean;
  desk?: DeskCase;
  /** The year's open case, where there is one, and the year it is for (its renewal cycle). */
  caseId?: string;
  cycleId?: string;
  /**
   * After a bounce: the desk's send its destination never got, which the organization's new
   * authorization sends again (the read offers it while the assignment stays dispatched). The row
   * then says W3c and offers N13, as desk-15 does.
   */
  authorizeAgain: boolean;
  /**
   * Whether the case's latest send, the desk's own, is uncertain: the desk sends nothing more until
   * the organization says what happened (S18), so the row says no line of the desk's and Next due's
   * Check what happened speaks (review-143's F1).
   */
  deskSendUncertain: boolean;
  /** W5's day: the desk's send the email service took, in the filing's zone. */
  sentDay?: string;
  /** Whether the organization's own authorization of the case is in force (N3). */
  ownAuthorizationInForce: boolean;
  /** Givmo's AI setting as served: off, the desk can't act (Q10; frames 21, 21b). */
  aiOff: boolean;
}

/** Every read the desk's row needs, keyed as the panel and the send page key them. */
export function usePanelDesk(
  obligation: ComplianceObligation | undefined,
  next: NextDate | undefined,
): PanelDesk {
  const { orgRef, isReady, moduleStanding, operations } = useCompliance();
  const ref = isReady && moduleStanding === 'available' ? orgRef : null;
  const target = next !== undefined ? preparationTargetFor(next) : undefined;
  const cases = useQuery({
    queryKey: filingCasesQueryKey(ref, obligation?.obligationId),
    queryFn: () => listAllFilingCases(operations, ref ?? '', obligation?.obligationId ?? ''),
    enabled: ref !== null && obligation !== undefined && target !== undefined,
  });
  const openCase =
    target !== undefined ? caseForTarget(extractFilingCases(cases.data), target) : undefined;
  const standingRead = useQuery({
    queryKey: deskStandingQueryKey(ref),
    queryFn: () => getDeskStanding(operations, ref ?? ''),
    enabled: ref !== null && obligation !== undefined && target !== undefined,
  });
  const standing = decodeDeskStanding(standingRead.data);
  // A case's desk block is read only where the organization has or had an agreement: a hand-over
  // needs one in force, and the read keeps the last one once it is off, so with none ever recorded
  // (or the desk dark) no case has anything of the desk's to show.
  const agreed = standing?.standing !== undefined;
  const submissionRead = useQuery({
    queryKey: submissionQueryKey(ref, openCase?.caseId),
    queryFn: () => getCaseSubmission(operations, ref ?? '', openCase?.caseId ?? ''),
    enabled: ref !== null && openCase !== undefined && agreed,
  });
  const ai = useQuery({
    queryKey: complianceAiSettingQueryKey(ref),
    queryFn: () => operations.getAiSetting(ref ?? ''),
    enabled: ref !== null,
  });
  const desk = decodeDeskCase(submissionRead.data);
  const submission = decodeSubmission(submissionRead.data);
  const holds = deskHolds(desk);
  const taken = submission?.attempts
    .filter(
      (attempt) => attempt.dispatchedThrough === 'desk' && attempt.state === 'provider_accepted',
    )
    .at(-1);
  const authorization = submission?.authorization;
  const stage = submission !== undefined ? submissionStage(submission) : undefined;
  const bounced =
    submission !== undefined &&
    stage?.kind === 'sent' &&
    followThroughOf(submission, { attemptId: stage.attempt.attemptId })?.state === 'not_delivered';
  return {
    offered: deskOffered(
      desk,
      openCase !== undefined,
      standing,
      obligation !== undefined
        ? { jurisdictionCode: obligation.jurisdictionCode, ruleId: obligation.ruleId }
        : undefined,
    ),
    holds,
    ...(desk !== undefined ? { desk } : {}),
    ...(openCase !== undefined ? { caseId: openCase.caseId } : {}),
    ...(openCase?.renewalCycleId !== undefined ? { cycleId: openCase.renewalCycleId } : {}),
    authorizeAgain: holds && bounced && submission?.abilities.authorize === true,
    deskSendUncertain: stage?.kind === 'uncertain' && stage.attempt.dispatchedThrough === 'desk',
    ...(taken?.committedAt !== undefined ? { sentDay: shortDateIn(taken.committedAt) } : {}),
    ownAuthorizationInForce:
      authorization !== undefined &&
      authorization.state === 'active' &&
      authorization.mode !== 'givmo',
    aiOff: decodeComplianceAiSetting(ai.data)?.givmoAiEnabled === false,
  };
}

type DeskDialogKind = 'hand-over' | 'take-back' | 'ai-off';

/**
 * Frame 21a's row and, while Givmo has the filing, the desk's status and its acts. `onOpenForm`
 * and `onReviewAndAuthorize` are the panel's own ways to the form and to the send review.
 */
export function PanelDeskRow({
  panelDesk,
  obligation,
  next,
  onOpenForm,
  onReviewAndAuthorize,
}: {
  panelDesk: PanelDesk;
  obligation: ComplianceObligation;
  next: NextDate | undefined;
  onOpenForm?: (() => void) | undefined;
  onReviewAndAuthorize?: (() => void) | undefined;
}) {
  const { orgRef, operations, canWrite, canSendForSignature, personName, complianceSettings } =
    useCompliance();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<DeskDialogKind | null>(null);
  const { desk, holds, caseId } = panelDesk;
  const ref = orgRef ?? '';

  const settle = (answer: unknown, actedOn: string) => {
    queryClient.setQueryData(submissionQueryKey(orgRef, actedOn), answer);
    void queryClient.invalidateQueries({ queryKey: ['compliance'] });
    setDialog(null);
  };
  const handOver = useMutation({
    mutationFn: async () => {
      let acted = caseId;
      if (acted === undefined) {
        // A year with no case yet: open it first, as Prepare the form does, then hand it over.
        const target = next !== undefined ? preparationTargetFor(next) : undefined;
        if (target === undefined) throw new Error(TRY_AGAIN_LINE);
        const opened = decodeOpenedFilingCase(
          await operations.openFilingCase(ref, obligation.obligationId, openCaseBody(target)),
        );
        if (opened === undefined) throw new Error(TRY_AGAIN_LINE);
        acted = opened.caseId;
      }
      return { answer: await operations.handFilingToDesk(ref, acted), acted };
    },
    onSuccess: ({ answer, acted }) => settle(answer, acted),
    onError: (cause) => {
      // The year's case may have opened before the hand-over failed: read it again.
      if (caseId === undefined) void queryClient.invalidateQueries({ queryKey: ['compliance'] });
      if (isGivmoAiOff(cause)) setDialog('ai-off');
    },
  });
  const takeBack = useMutation({
    mutationFn: async () => operations.takeFilingFromDesk(ref, caseId ?? ''),
    onSuccess: (answer) => settle(answer, caseId ?? ''),
  });
  const resume = useMutation({
    mutationFn: async () => operations.resumeDesk(ref, caseId ?? ''),
    onSuccess: (answer) => settle(answer, caseId ?? ''),
  });

  if (!panelDesk.offered) return null;
  const assignment = desk?.assignment;
  const givmoChosen = holds || assignment?.state === 'completed';
  // review-143's F1, in its order: while the desk's own send is uncertain, no line of the desk's
  // (Next due's Check what happened speaks); else, while the desk holds a filing it hasn't sent with
  // no agreement in force (the case's desk block serves none: a turn-off left the work to its send),
  // R5. A filing Givmo already sent stays with Givmo (W6, D-CCM-92): W5 until the agency's
  // confirmation is recorded, then W6. Then desk-15's rule: after a bounce, W3c and N13 in place of
  // the desk's own line.
  const status =
    holds && panelDesk.deskSendUncertain
      ? undefined
      : holds && desk?.standingInForce === false && assignment?.state !== 'dispatched'
        ? R5
        : panelDesk.authorizeAgain
          ? w3(W3C_NEED)
          : deskStatusLine(assignment, panelDesk.sentDay);
  const handedBy =
    assignment?.handedByUserId !== undefined ? personName?.(assignment.handedByUserId) : undefined;
  const attribution =
    holds && handedBy !== undefined && assignment?.handedAt !== undefined
      ? n4(handedBy, shortDateIn(assignment.handedAt))
      : undefined;
  // Before any assignment the offer is the hand-over's: where the read serves the case, its own
  // ability; on a year with no case yet, the viewer's right to make changes (J-5 (a)).
  const mayHandOver = desk !== undefined ? desk.abilities.handOver : canWrite === true;
  const resumeFailure =
    resume.isError && dialog === null ? serverMessage(resume.error, TRY_AGAIN_LINE) : undefined;
  // Frame 21 for whoever can turn the AI on (a Compliance Owner or a Global Admin), 21b for anyone
  // else. The Owner line never shows on a guess: unreported reads as frame 21, and the Settings
  // page shows the AI card only to those who may change it.
  const mayTurnAiOn = canSendForSignature !== false;
  const settingsDoor = mayTurnAiOn ? complianceSettings : undefined;
  // The take-back, from N8 or the tile alike: where the read offers it, and never once the desk's
  // send is taken (the ruled table: no acts once sent; review-143's F2).
  const mayTakeBack = desk?.abilities.takeBack === true && assignment?.state !== 'dispatched';

  const acts: ReactNode[] = [];
  if (needsAuthorization(desk) || panelDesk.authorizeAgain) {
    if (onReviewAndAuthorize !== undefined)
      acts.push(
        <Button
          key="authorize"
          appearance="saas"
          tone="primary"
          size="sm"
          onClick={onReviewAndAuthorize}
          testId={`${PREFIX}-review-and-authorize`}
        >
          {N13}
        </Button>,
      );
  } else if (assignment?.state === 'awaiting_organization') {
    // The read can't say whether the need is met (continuing is offered while the work awaits
    // the organization), so the act that meets it leads, and continuing is second (Q9).
    if (onOpenForm !== undefined)
      acts.push(
        <Button
          key="form"
          appearance="saas"
          tone="primary"
          size="sm"
          onClick={onOpenForm}
          testId={`${PREFIX}-open-form`}
        >
          {OPEN_THE_FORM}
        </Button>,
      );
    if (desk?.abilities.resume === true)
      acts.push(
        <Button
          key="resume"
          appearance="saas"
          tone="secondary"
          size="sm"
          loading={resume.isPending}
          onClick={() => resume.mutate()}
          testId={`${PREFIX}-resume`}
        >
          {N5}
        </Button>,
      );
  }
  if (mayTakeBack)
    acts.push(
      <Button
        key="take-back"
        appearance="saas"
        tone="secondary"
        size="sm"
        onClick={() => setDialog('take-back')}
        testId={`${PREFIX}-take-back`}
      >
        {N8}
      </Button>,
    );

  return (
    <section className="flex flex-col gap-10" {...testIdProp(PREFIX)}>
      <h3 className="text-s14 font-w600 text-text-strong">{WHO_FILES_IT}</h3>
      <RadioTileGroup
        value={givmoChosen ? 'givmo' : 'organization'}
        onValueChange={(chosen) => {
          if (chosen === 'givmo' && !holds && mayHandOver)
            setDialog(panelDesk.aiOff ? 'ai-off' : 'hand-over');
          if (chosen === 'organization' && holds && mayTakeBack) setDialog('take-back');
        }}
        aria-label={WHO_FILES_IT}
        className="flex flex-col gap-10"
      >
        <RadioTile
          value="organization"
          label={WE_HANDLE_IT}
          description={WE_HANDLE_IT_LINE}
          testId={`${PREFIX}-ours`}
        />
        <RadioTile
          value="givmo"
          label={GIVMO_HANDLES_IT}
          description={GIVMO_HANDLES_IT_LINE}
          // Not offered where the read offers no hand-over (the agreement turned off, R5).
          disabled={!givmoChosen && !mayHandOver}
          testId={`${PREFIX}-givmo`}
        />
      </RadioTileGroup>
      {status !== undefined && (
        <div
          className="flex flex-col gap-8 rounded-lg bg-surface-sunken px-16 py-12"
          {...testIdProp(`${PREFIX}-status`)}
        >
          <p className="text-s14 leading-h15 text-text-strong" {...testIdProp(`${PREFIX}-line`)}>
            {status}
          </p>
          {holds && panelDesk.aiOff && (
            <p
              className="text-s13 leading-h15 text-text-secondary"
              {...testIdProp(`${PREFIX}-ai-off`)}
            >
              {AI_OFF_LINE}
            </p>
          )}
          {attribution !== undefined && (
            <p className="text-s12 text-text-tertiary" {...testIdProp(`${PREFIX}-handed`)}>
              {attribution}
            </p>
          )}
          {acts.length > 0 && <div className="flex flex-wrap gap-8 pt-4">{acts}</div>}
          {resumeFailure !== undefined && (
            <ErrorMessage testId={`${PREFIX}-failure`}>{resumeFailure}</ErrorMessage>
          )}
        </div>
      )}
      <DeskDialog
        open={dialog === 'hand-over'}
        title={N1}
        lines={[RUN_BY_AI, ...(panelDesk.ownAuthorizationInForce ? [N3] : [])]}
        confirm={N2}
        pending={handOver.isPending}
        failure={
          handOver.isError && !isGivmoAiOff(handOver.error)
            ? serverMessage(handOver.error, TRY_AGAIN_LINE)
            : undefined
        }
        onConfirm={() => handOver.mutate()}
        onClose={() => {
          handOver.reset();
          setDialog(null);
        }}
        testId={`${PREFIX}-hand-over`}
      />
      <DeskDialog
        open={dialog === 'take-back'}
        title={N6}
        lines={[N7]}
        confirm={N8}
        pending={takeBack.isPending}
        failure={takeBack.isError ? serverMessage(takeBack.error, TRY_AGAIN_LINE) : undefined}
        onConfirm={() => takeBack.mutate()}
        onClose={() => {
          takeBack.reset();
          setDialog(null);
        }}
        testId={`${PREFIX}-take-back-dialog`}
      />
      {/* Frames 21 (a Compliance Owner or a Global Admin) and 21b (anyone else). */}
      <DeskDialog
        open={dialog === 'ai-off'}
        title={AI_OFF_TITLE}
        lines={[`${RUN_BY_AI} ${AI_OFF_LINE}`, ...(mayTurnAiOn ? [] : [ONLY_OWNERS_TURN_ON])]}
        confirm={settingsDoor !== undefined ? GO_TO_SETTINGS : GOT_IT}
        cancel={settingsDoor !== undefined ? CANCEL : null}
        pending={false}
        onConfirm={() => {
          handOver.reset();
          setDialog(null);
          settingsDoor?.open();
        }}
        onClose={() => {
          handOver.reset();
          setDialog(null);
        }}
        testId={`${PREFIX}-ai-off-dialog`}
      />
    </section>
  );
}

/** One desk dialog, in the built dialogs' idiom: its title, its lines, its act, and Cancel. */
export function DeskDialog({
  open,
  title,
  lines,
  confirm,
  cancel = CANCEL,
  pending,
  failure,
  onConfirm,
  onClose,
  testId,
}: {
  open: boolean;
  title: string;
  lines: readonly string[];
  confirm: string;
  /** Cancel's label; `null` for a dialog with one act (frame 21b's "Got it"). */
  cancel?: string | null;
  pending: boolean;
  failure?: string | undefined;
  onConfirm: () => void;
  onClose: () => void;
  testId: string;
}) {
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next && !pending) onClose();
      }}
    >
      {open && (
        <Dialog.Content width={460} className="flex flex-col gap-16" testId={testId}>
          <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
            {title}
          </Dialog.Title>
          {lines.map((line) => (
            <Dialog.Description
              key={line}
              className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
            >
              {line}
            </Dialog.Description>
          ))}
          {failure !== undefined && (
            <ErrorMessage testId={`${testId}-failure`}>{failure}</ErrorMessage>
          )}
          <div className="flex flex-wrap gap-8">
            <Button
              appearance="saas"
              tone="primary"
              size="md"
              loading={pending}
              onClick={onConfirm}
              testId={`${testId}-confirm`}
            >
              {confirm}
            </Button>
            {cancel !== null && (
              <Button
                appearance="saas"
                tone="ghost"
                size="md"
                disabled={pending}
                onClick={onClose}
                testId={`${testId}-cancel`}
              >
                {cancel}
              </Button>
            )}
          </div>
        </Dialog.Content>
      )}
    </Dialog.Root>
  );
}
